/**
 * cart-gift-coupon.js
 * -----------------------------------------------------------------------------
 * Brinde automático por cupom (ex.: PRIMEIRACOMPRA): quando o código configurado
 * em Configurações do tema > Cart Drawer estiver aplicado ao carrinho:
 *  - 1 produto configurado  -> adiciona esse produto sozinho.
 *  - 2+ produtos configurados -> abre um popup pro cliente escolher qual brinde
 *    quer; enquanto não escolher, mostra um link "Escolher meu brinde".
 * Quando o cupom for removido, o brinde escolhido é removido junto.
 *
 * Código e lista de produtos vêm de data-attributes/JSON renderizados pelo
 * Liquid em templates/cart.jsonData.liquid (settings.gift_coupon_*), então tudo
 * é configurável pelo editor de temas, sem precisar tocar neste arquivo.
 *
 * Como funciona: o drawer do tema é recriado via `$("[data-drawer-body]").load(...)`
 * (AJAX assíncrono) toda vez que o carrinho muda — então mexer no DOM (abrir
 * popup, mostrar botão) logo depois de chamar buildCart é uma corrida: o load()
 * troca o HTML por baixo e desfaz qualquer coisa que a gente tenha acabado de
 * setar. Por isso escutamos o evento global `ajaxComplete` do jQuery filtrando
 * pela URL "view=jsonData" — ele só dispara DEPOIS que o novo HTML já foi
 * inserido no DOM, então é o momento seguro de comparar o estado do cupom com a
 * presença do brinde e corrigir via /cart/add.js ou /cart/change.js.
 *
 * A linha adicionada por este script leva a propriedade oculta "_yv_gift_coupon"
 * (prefixo "_" some da UI do carrinho automaticamente, ver cart.jsonData.liquid).
 *
 * Limite de 1 brinde: os produtos de brinde são exclusivos (R$ 0,00, acessíveis
 * pela URL mesmo "não listados"), então QUALQUER linha de um produto configurado
 * conta como brinde — com ou sem a propriedade. A cada sincronização o carrinho
 * é corrigido pra ter no máximo 1 unidade no total (cupom aplicado) ou nenhuma
 * (cupom ausente). Cobre: quantidade aumentada, add repetido que a Shopify soma
 * na mesma linha, os dois brindes escolhidos, e brinde adicionado pela página
 * do produto.
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__yvGiftCouponInstalled) return;
  window.__yvGiftCouponInstalled = true;

  var PROPERTY_KEY = '_yv_gift_coupon';
  var busy = false;

  function getConfig() {
    var root = document.querySelector('[data-yv-cart-coupon]');
    if (!root) return null;

    var code = (root.getAttribute('data-yv-gift-code') || '').trim().toUpperCase();
    if (!code) return null;

    var optionsEl = document.querySelector('[data-yv-gift-options]');
    var options = [];
    if (optionsEl) {
      try {
        options = JSON.parse(optionsEl.textContent) || [];
      } catch (e) {
        options = [];
      }
    }
    if (!options.length) return null;

    return { code: code, options: options };
  }

  function isCouponApplied(cart, code) {
    var byLevel = (cart.cart_level_discount_applications || []).some(function (d) {
      return (d.title || '').toUpperCase() === code;
    });
    if (byLevel) return true;

    return (cart.discount_codes || []).some(function (d) {
      return d.applicable && (d.code || '').toUpperCase() === code;
    });
  }

  function findGiftLines(cart, config) {
    var productIds = {};
    var variantIds = {};
    config.options.forEach(function (opt) {
      if (opt.productId) productIds[opt.productId] = true;
      if (opt.variantId) variantIds[opt.variantId] = true;
    });

    return (cart.items || []).filter(function (item) {
      return productIds[item.product_id] ||
        variantIds[item.variant_id] ||
        (item.properties && item.properties[PROPERTY_KEY] === config.code);
    });
  }

  // Monta o /cart/update.js que deixa o carrinho com no máximo `max` unidade de
  // brinde. Mantém a linha marcada mais recente (o /cart.js lista do item mais
  // novo pro mais antigo), ou seja, a última escolha do cliente. Retorna null se
  // o carrinho já está dentro do limite.
  function buildGiftLimitUpdates(giftLines, config, max) {
    var total = giftLines.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    if (total <= max) return null;

    var keeper = null;
    if (max > 0) {
      keeper = giftLines.find(function (item) {
        return item.properties && item.properties[PROPERTY_KEY] === config.code;
      }) || giftLines[0];
    }

    var updates = {};
    giftLines.forEach(function (item) {
      updates[item.key] = item === keeper ? max : 0;
    });
    return updates;
  }

  function refreshAfterMutation() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (freshCart) {
        if (typeof window.buildCart === 'function') window.buildCart(freshCart, true);
      });
  }

  function logIfFailed(label) {
    return function (res) {
      if (!res.ok) {
        res.json().then(function (data) {
          console.warn('[cart-gift-coupon] ' + label + ' falhou:', (data && (data.description || data.message)) || res.status);
        }).catch(function () {
          console.warn('[cart-gift-coupon] ' + label + ' falhou com status', res.status);
        });
      }
      return res;
    };
  }

  function addGift(variantId, code) {
    var properties = {};
    properties[PROPERTY_KEY] = code;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: properties }] })
    }).then(logIfFailed('adicionar brinde'));
  }

  // Um único request pra todas as linhas (chaves de linha como índice do
  // `updates`), pra não depender de índices que mudam a cada remoção.
  function setGiftQuantities(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(logIfFailed('limitar brinde'));
  }

  // --- Popup de escolha (quando há 2+ produtos configurados) ---

  function dismissedKey(code) {
    return 'yv_gift_dismissed_' + code;
  }

  function isDismissed(code) {
    try {
      return sessionStorage.getItem(dismissedKey(code)) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDismissed(code, value) {
    try {
      if (value) sessionStorage.setItem(dismissedKey(code), '1');
      else sessionStorage.removeItem(dismissedKey(code));
    } catch (e) { /* storage indisponível: sem persistência, tudo bem */ }
  }

  // Janela "mostrando mensagem de sucesso": guardamos ATÉ QUANDO (timestamp
  // absoluto) a mensagem deve ficar visível, em vez de um flag de uma vez só.
  // Isso é necessário porque existe outro script no tema (koala-gift-cart-
  // refresh.js) que também observa /cart/add.js e dispara seu PRÓPRIO refresh
  // do drawer ~500ms depois, sem coordenação com a gente — e como o drawer é
  // recriado do zero a cada refresh, esse refresh extra apagaria a mensagem
  // antes da hora. Com uma janela de tempo, toda vez que o syncGift rodar
  // (nosso refresh OU o de qualquer outro script) ele reabre a mensagem pelo
  // tempo que ainda falta, então nenhum refresh concorrente consegue derrubar
  // ela antes do prazo.
  var GIFT_SUCCESS_DURATION_MS = 1500;

  function showSuccessUntilKey(code) {
    return 'yv_gift_show_success_until_' + code;
  }

  function markShowSuccessWindow(code) {
    try {
      sessionStorage.setItem(showSuccessUntilKey(code), String(Date.now() + GIFT_SUCCESS_DURATION_MS));
    } catch (e) { /* storage indisponível: sem persistência, tudo bem */ }
  }

  function clearShowSuccessWindow(code) {
    try {
      sessionStorage.removeItem(showSuccessUntilKey(code));
    } catch (e) { /* storage indisponível: sem persistência, tudo bem */ }
  }

  function getShowSuccessRemaining(code) {
    try {
      var until = parseInt(sessionStorage.getItem(showSuccessUntilKey(code)), 10);
      if (!until) return 0;
      return Math.max(0, until - Date.now());
    } catch (e) {
      return 0;
    }
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function renderPickerList(options) {
    var list = document.querySelector('[data-yv-gift-picker-list]');
    if (!list) return;

    list.innerHTML = options.map(function (opt) {
      var img = opt.image
        ? '<img src="' + escapeHtml(opt.image) + '" alt="" width="52" height="52">'
        : '';
      return (
        '<div class="yv-gift-picker-item">' +
          img +
          '<div class="yv-gift-picker-item-info">' +
            '<p>' + escapeHtml(opt.title) + '</p>' +
            '<p class="yv-gift-picker-item-price">' + escapeHtml(opt.priceFormatted) + '</p>' +
          '</div>' +
          '<button type="button" class="yv-gift-picker-choose" data-yv-gift-choose="' + escapeHtml(opt.variantId) + '">Escolher</button>' +
        '</div>'
      );
    }).join('');
  }

  function openPicker(options) {
    renderPickerList(options);
    var list = document.querySelector('[data-yv-gift-picker-list]');
    var success = document.querySelector('[data-yv-gift-picker-success]');
    if (list) list.hidden = false;
    if (success) success.hidden = true;
    var overlay = document.querySelector('[data-yv-gift-picker-overlay]');
    if (overlay) overlay.hidden = false;
  }

  var successCloseTimer = null;

  function openPickerSuccess(durationMs) {
    var list = document.querySelector('[data-yv-gift-picker-list]');
    var success = document.querySelector('[data-yv-gift-picker-success]');
    if (list) list.hidden = true;
    if (success) success.hidden = false;
    var overlay = document.querySelector('[data-yv-gift-picker-overlay]');
    if (overlay) overlay.hidden = false;

    // Sempre reagenda pro tempo que falta (nunca reinicia do zero), pra um
    // refresh concorrente (ver nota da janela de sucesso acima) não estender
    // a mensagem além do prazo original.
    clearTimeout(successCloseTimer);
    successCloseTimer = setTimeout(closePicker, durationMs);
  }

  function closePicker() {
    var overlay = document.querySelector('[data-yv-gift-picker-overlay]');
    if (overlay) overlay.hidden = true;
  }

  function showTrigger(show) {
    var trigger = document.querySelector('[data-yv-gift-picker-trigger]');
    if (trigger) trigger.hidden = !show;
  }

  function chooseGift(variantId, code) {
    setDismissed(code, false);
    // Mostra a mensagem NO MESMO INSTANTE do clique (não espera o /cart/add.js
    // responder) e já inicia a contagem de 1,5s. A janela gravada em
    // sessionStorage (ver markShowSuccessWindow) é o que garante que o refresh
    // disparado logo abaixo — e qualquer refresh concorrente de outro script,
    // tipo koala-gift-cart-refresh.js — não feche ela antes da hora: toda vez
    // que o drawer for recriado, syncGift reabre a mensagem pelo tempo restante.
    markShowSuccessWindow(code);
    openPickerSuccess(GIFT_SUCCESS_DURATION_MS);

    busy = true;
    addGift(variantId, code)
      .then(refreshAfterMutation)
      .catch(function () { /* rede falhou: não quebra a loja */ })
      .finally(function () { busy = false; });
  }

  function applyCoupon(code) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    }).then(logIfFailed('aplicar cupom'));
  }

  // Clique no botão "Escolher meu brinde": só garante o cupom aplicado
  // (idempotente se já estiver) e pede um refresh do drawer. A resolução do
  // brinde em si (abrir o popup, ou adicionar direto se só houver 1 produto)
  // fica por conta do syncGift, disparado depois que o refresh terminar de
  // verdade — ver installAjaxHook. Não decidimos isso aqui pra não abrir o
  // popup ANTES do refresh, que aí o próprio refresh fecharia ele de novo.
  function applyCouponAndGetGift(config) {
    setDismissed(config.code, false);
    applyCoupon(config.code)
      .then(refreshAfterMutation)
      .catch(function () { /* rede falhou: cliente pode tentar de novo */ });
  }

  document.addEventListener('click', function (e) {
    var closeBtn = e.target.closest('[data-yv-gift-picker-close]');
    var overlay = e.target.closest('[data-yv-gift-picker-overlay]');
    var trigger = e.target.closest('[data-yv-gift-picker-trigger]');
    var chooseBtn = e.target.closest('[data-yv-gift-choose]');

    if (closeBtn || (overlay && e.target === overlay)) {
      closePicker();
      var config = getConfig();
      if (config) setDismissed(config.code, true);
      return;
    }

    if (trigger) {
      var config2 = getConfig();
      if (config2) applyCouponAndGetGift(config2);
      return;
    }

    if (chooseBtn) {
      var config3 = getConfig();
      if (!config3) return;
      var variantId = parseInt(chooseBtn.getAttribute('data-yv-gift-choose'), 10);
      chooseGift(variantId, config3.code);
    }
  });

  // --- Sincronização automática a cada render do carrinho ---

  function syncGift(cart) {
    if (busy) return;

    var config = getConfig();
    if (!config) {
      showTrigger(false);
      return;
    }

    var applied = isCouponApplied(cart, config.code);
    var giftLines = findGiftLines(cart, config);

    if (giftLines.length) {
      // brinde já escolhido/adicionado
      showTrigger(false);

      // cupom aplicado -> no máximo 1 unidade; cupom removido -> nenhuma
      var updates = buildGiftLimitUpdates(giftLines, config, applied ? 1 : 0);
      if (updates) {
        busy = true;
        setGiftQuantities(updates)
          .then(refreshAfterMutation)
          .catch(function () { /* rede falhou: não quebra a loja */ })
          .finally(function () { busy = false; });
      }

      if (!applied) {
        setDismissed(config.code, false);
        clearShowSuccessWindow(config.code);
        closePicker();
      } else {
        // a mensagem já foi mostrada no clique (ver chooseGift); aqui só
        // garantimos que ela sobreviva a QUALQUER refresh que role nesse meio
        // tempo (nosso ou de outro script) — reabre pelo tempo que ainda
        // falta, nunca reinicia os 1,5s do zero
        var remaining = getShowSuccessRemaining(config.code);
        if (remaining > 0) openPickerSuccess(remaining);
      }
      return;
    }

    // ainda não tem brinde: botão "Escolher meu brinde" sempre visível
    // (funciona como atalho pra aplicar o cupom + resolver o brinde de uma vez),
    // independente do cupom já estar aplicado e de haver 1 ou vários produtos.
    showTrigger(true);

    if (!applied) return;

    if (config.options.length === 1) {
      // produto único: some sozinho quando o cupom já estiver aplicado
      busy = true;
      addGift(config.options[0].variantId, config.code)
        .then(refreshAfterMutation)
        .catch(function () { /* rede falhou: não quebra a loja */ })
        .finally(function () { busy = false; });
    } else if (!isDismissed(config.code)) {
      // 2+ produtos: só abre o popup sozinho se o cupom já estiver aplicado
      // (ex.: digitado manualmente) e o cliente ainda não tiver fechado antes
      openPicker(config.options);
    }
  }

  // O drawer é recriado via $("[data-drawer-body]").load(...?view=jsonData...)
  // dentro do buildCart nativo do tema. "ajaxComplete" do jQuery dispara DEPOIS
  // que esse load() já trocou o HTML — momento seguro pra sincronizar.
  function onDrawerRefreshed() {
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(syncGift)
      .catch(function () { /* rede falhou: tenta de novo no próximo refresh */ });
  }

  function installAjaxHook() {
    if (!window.jQuery || window.jQuery.__yvGiftAjaxHooked) return;
    window.jQuery.__yvGiftAjaxHooked = true;

    window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
      if (settings && typeof settings.url === 'string' && settings.url.indexOf('view=jsonData') !== -1) {
        onDrawerRefreshed();
      }
    });
  }

  document.addEventListener('DOMContentLoaded', installAjaxHook);
  if (document.readyState !== 'loading') installAjaxHook();
})();
